import { create } from 'zustand';
import { InterviewQuestions } from '@/types';
import { isInterviewQuestionsEmpty } from '@/utils/questionGroupHelpers';
import { createInterviewQuestionsSchema } from '@/schemas';

export interface RequiredField {
  key: string;
  label: string;
  isEmpty: boolean;
}

type State = {
  requiredFields: RequiredField[];
  emptyFieldsLabels: string[];
};

type Action = {
  setRequiredFields: (fields: RequiredField[]) => void;
};

// The publish-required fields and their i18n labels. Single source of truth for
// both the live (form) computation on the Design page and the hydration from
// saved project data on every other route (TELV1-509).
const REQUIRED_FIELD_DEFS: { key: string; label: string }[] = [
  { key: 'interviewer_name', label: 'interviewerName' },
  { key: 'company_name', label: 'companyName' },
  { key: 'interview_goal', label: 'myResearchObjective' },
  { key: 'interview_audience', label: 'interviewAudience' },
  { key: 'language', label: 'primaryLanguage' },
  {
    key: 'allow_language_selection',
    label: 'allowParticipantsToChooseTheLanguage',
  },
  { key: 'analysis_language', label: 'analysisLanguage' },
  { key: 'privacy_policy', label: 'privacyPolicy' },
  { key: 'tone_of_voice', label: 'toneOfVoice' },
  { key: 'opening_message', label: 'openingMessage' },
  { key: 'interview_questions', label: 'interviewQuestions' },
  { key: 'respondents_max', label: 'maximumNumberOfRespondents' },
];

/**
 * Computes the required-field empty state from a project-like source — either
 * the live RHF form values (Design page) or the saved project (`singleProjectData`,
 * every route). Pure so both producers agree and the publish gate can never
 * depend on which page happened to mount (TELV1-509).
 */
export const computeRequiredFields = (
  source: Record<string, unknown> | undefined | null,
  // TELV1-186/421 — embed studies have different publish-readiness rules.
  // Passed explicitly (not read off `source`) because the live Design-page
  // caller feeds RHF form values, which do not carry `project_type`.
  isEmbed = false,
): RequiredField[] =>
  REQUIRED_FIELD_DEFS.map(({ key, label }) => {
    const value = source?.[key];

    let isEmpty: boolean;
    if (isEmbed && key === 'opening_message') {
      // TELV1-186: embed respondents arrive mid-survey, so the opening
      // message is never a missing required field. Mirrors the embed-aware
      // backend validation + Yup schema.
      isEmpty = false;
    } else if (key === 'allow_language_selection') {
      // `false` ("No") and `true` ("Yes") are real selections; only an
      // absent value (null/undefined) is empty.
      isEmpty = value === null || value === undefined;
    } else if (key === 'interview_questions') {
      const questionsStructure = value as InterviewQuestions | undefined;
      if (isEmbed) {
        // TELV1-421 — embed publish-readiness must match what Save accepts
        // (the full Yup tree incl. checkpoint label / adjacency / max-3),
        // else Publish can light up for a config Save would reject.
        isEmpty =
          isInterviewQuestionsEmpty(questionsStructure) ||
          !createInterviewQuestionsSchema(true).isValidSync(questionsStructure);
      } else {
        isEmpty = isInterviewQuestionsEmpty(questionsStructure);
      }
    } else {
      isEmpty = !value;
    }

    return { key, label, isEmpty };
  });

// With no source, every field is empty — the pre-hydration default.
const initialRequiredFields: RequiredField[] = computeRequiredFields(undefined);

const useProjectRequiredFieldsStore = create<State & Action>((set) => ({
  requiredFields: initialRequiredFields,
  emptyFieldsLabels: initialRequiredFields
    .filter((field) => field.isEmpty)
    .map((field) => field.label),

  setRequiredFields: (fields) => {
    set({ requiredFields: fields });
    const emptyFields = fields.filter((field) => field.isEmpty).map((field) => field.label);
    set({ emptyFieldsLabels: emptyFields });
  },
}));

export default useProjectRequiredFieldsStore;
